import { Controller } from "@hotwired/stimulus"

// Sequences the IDE's decorative animation.
//
// Upstream drives this from requestAnimationFrame with a queue of delays.
// The same idea, simpler: a list of steps, each `{ delay, run }`, played
// one after another with setTimeout. Pausing clears the pending timer;
// resuming schedules the current step again. Under reduced motion every
// step runs at once, so the finished state is what appears.
export default class extends Controller {
  static targets = [
    "playPause", "playIcon", "pauseIcon",
    "chat", "chatMessages", "chatMessage",
    "editor", "editorTab", "editorPanel"
  ]

  connect() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.paused = false
    this.restart()
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  // --- play / pause -------------------------------------------------------

  togglePlayPause() {
    if (this.done) {
      this.restart()
    } else if (this.paused) {
      this.paused = false
      this.schedule()
    } else {
      this.paused = true
      clearTimeout(this.timer)
    }
    this.syncButton()
  }

  syncButton() {
    const playing = !this.paused && !this.done
    this.playIconTarget.hidden = playing
    this.pauseIconTarget.hidden = !playing
    this.playPauseTarget.setAttribute("aria-label", playing ? "Pause animation" : "Play animation")
  }

  // --- sequencing ---------------------------------------------------------

  restart() {
    clearTimeout(this.timer)
    this.done = false
    this.paused = false
    this.steps = [...this.chatSteps(), ...this.editorSteps()]
    this.index = 0
    this.reset()
    if (this.reducedMotion) {
      for (const step of this.steps) step.run()
      this.finish()
    } else {
      this.schedule()
    }
    this.syncButton()
  }

  schedule() {
    if (this.index >= this.steps.length) {
      this.finish()
      return
    }
    const step = this.steps[this.index]
    this.timer = setTimeout(() => {
      step.run()
      this.index += 1
      this.schedule()
    }, step.delay)
  }

  finish() {
    this.done = true
    this.syncButton()
  }

  reset() {
    for (const message of this.chatMessageTargets) {
      message.classList.remove("IDE__Chat-message--visible", "IDE__Chat-message--faded")
    }
    if (this.hasChatMessagesTarget) this.chatMessagesTarget.scrollTop = 0
    for (const pre of this.activePanelLines()) pre.classList.remove("Animation--active")
  }

  chatSteps() {
    if (!this.hasChatTarget) return []
    const delay = Number(this.chatTarget.dataset.brandIdeDelay) || 3000
    const [first, ...rest] = this.chatMessageTargets
    const steps = []
    if (first) steps.push({ delay: 0, run: () => first.classList.add("IDE__Chat-message--visible") })
    steps.push({ delay: delay / 4, run: () => {} })
    rest.forEach((message, i) => {
      steps.push({
        delay,
        run: () => {
          if (i % 2 === 0) this.scrollMessageIntoView(message)
          // Everything before the newest message dims, then all clear at the end.
          first?.classList.add("IDE__Chat-message--faded")
          rest.slice(0, i).forEach(m => m.classList.add("IDE__Chat-message--faded"))
          message.classList.add("IDE__Chat-message--visible")
          if (i === rest.length - 1) {
            setTimeout(() => {
              for (const m of this.chatMessageTargets) m.classList.remove("IDE__Chat-message--faded")
            }, 100)
          }
        }
      })
    })
    return steps
  }

  editorSteps() {
    if (!this.hasEditorTarget) return []
    const lines = this.activePanelLines()
    const plain = lines.filter(pre => pre.dataset.hasSuggestion !== "true")
    const suggested = lines.filter(pre => pre.dataset.hasSuggestion === "true")
    const steps = plain.map(pre => ({ delay: 200, run: () => pre.classList.add("Animation--active") }))
    steps.push({ delay: 1000, run: () => suggested.forEach(pre => pre.classList.add("Animation--active")) })
    return steps
  }

  activePanelLines() {
    const panel = this.editorPanelTargets.find(p => !p.hidden)
    return panel ? Array.from(panel.querySelectorAll("pre")) : []
  }

  scrollMessageIntoView(message) {
    const container = this.chatMessagesTarget
    const rect = message.getBoundingClientRect()
    const box = container.getBoundingClientRect()
    if (rect.top < box.top || rect.bottom > box.bottom) container.scrollTop += rect.top - box.top
  }

  // --- editor tabs --------------------------------------------------------

  selectTab(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.editorTabTargets.forEach((tab, i) => {
      const active = i === index
      tab.classList.toggle("IDE__Editor-tab--active", active)
      tab.setAttribute("aria-selected", String(active))
      tab.tabIndex = active ? 0 : -1
    })
    this.editorPanelTargets.forEach((panel, i) => { panel.hidden = i !== index })
    event.currentTarget.scrollIntoView({ block: "nearest", inline: "start" })
    this.restart()
  }
}
